import {Alert, Backdrop, Box, ButtonGroup, CircularProgress, useTheme} from '@mui/material';
import dayjs from 'dayjs';
import {t} from 'i18next';
import update from 'immutability-helper';
import * as _ from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as TooltipRecharts,
  XAxis,
  YAxis,
} from 'recharts';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {MAX_BAR_SIZE} from '../../../constants/common';
import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {
  HazardAIMachineUtilizationChartQuery,
  HazardAIMachineUtilizationChartResponse,
} from '../../../interfaces/HazardAIMachineUtilizationChart';
import reduxSelectors from '../../../redux/selectors';
import {
  usePanel,
  usePanelFilter,
} from '../../dashboards/entities/DashboardEntityContext';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {MachineTypeSelect} from '../../selectors/MachineTypeSelect';
import {SafeyeNodeSelector} from '../../selectors/SafeyeNodeSelector';
import {SitesSelect} from '../../selectors/SitesSelect';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';
import HazardAISummaryGrid from './HazardAISummaryGrid';
import HazardAISummaryGridExportButton from './HazardAISummaryGridExportButton';

const DEFAULT_SHOWN_FIELDS = [
  'machine_name',
  'site_name',
  'machine_type',
  'operation_time',
  'operating_no_movement',
  'idle_time',
];

enum ChartLabels {
  operation_time = 'Operation Time',
  operating_no_movement = 'Operating with No Movement',
  idle_time = 'Idle Time',
}

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value?: DashboardPanelData) => void;
}

export const HazardAIMachineUtilizationChart = (props?: Props) => {
  const isDarkMode = useSelector(reduxSelectors.app.getIsDarkMode);
  const theme = useTheme();
  const [panel] = usePanel();
  const {filter, setFilter} = usePanelFilter();

  const [fetchedData, setFetchedData] =
    useState<HazardAIMachineUtilizationChartResponse[]>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);
  const [hasData, setHasData] = useState(false);

  const params = useMemo<HazardAIMachineUtilizationChartQuery>(
    () => ({
      date_start:
        filter.params?.date_start ??
        props?.value?.params?.date_start ??
        dayjs().format('YYYY-MM-DD'),
      date_end:
        filter.params?.date_end ??
        props?.value?.params?.date_end ??
        dayjs().format('YYYY-MM-DD'),
      safeye_node_id: _.has(filter.params, 'safeye_node_ids')
        ? filter.params?.safeye_node_ids
        : props?.value?.params?.safeye_node_id,
      site_id: _.has(filter.params, 'site_ids')
        ? filter.params?.site_ids
        : props?.value?.params?.site_id,
      type_id: _.has(filter.params, 'type_ids')
        ? (filter.params?.type_ids as number[] | null)
        : props?.value?.params?.type_id != null
          ? [props.value.params.type_id].flat()
          : null,
    }),
    [filter]
  );

  const data = useMemo(() => {
    if (fetchedData) {
      const totalOpTime = fetchedData.reduce((acc, cur) => {
        return acc + Number(cur.operation_time);
      }, 0);

      const totalNoMovement = fetchedData.reduce((acc, cur) => {
        return acc + Number(cur.operating_no_movement);
      }, 0);

      const totalIdleTime = fetchedData.reduce((acc, cur) => {
        return acc + Number(cur.idle_time);
      }, 0);

      return [
        {name: ChartLabels.operation_time, value: Number(totalOpTime)},
        {name: ChartLabels.operating_no_movement, value: Number(totalNoMovement)},
        {name: ChartLabels.idle_time, value: Number(totalIdleTime)},
      ];
    }
  }, [fetchedData]);

  useEffect(() => {
    if (params) {
      fetchData(params);
      props?.onUpdate?.({...props.value, params});
    }
  }, [params]);

  const fetchData = useCallback(
    async (params: HazardAIMachineUtilizationChartQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const endpoint = `${apiBaseUrl}/hazard-ai/machine-utilization`;
        const resp = await API.get<HazardAIMachineUtilizationChartResponse[]>(
          endpoint,
          {params}
        );
        setFetchedData(resp.data);
        setHasData(!!resp.data?.length);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_1dde37dd');
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  const renderCustomizedLabel = (options: any) => {
    const radius =
      options.innerRadius + (options.outerRadius - options.innerRadius) * 0.5;
    const x =
      options.cx + radius * Math.cos((-options.midAngle * Math.PI) / 180);
    const y =
      options.cy + radius * Math.sin((-options.midAngle * Math.PI) / 180);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > options.cx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {`${(options.percent * 100).toFixed(2)} %`}
      </text>
    );
  };

  const CustomTooltip = (options: any) => {
    if (options.active && options.payload?.length) {
      const item = options.payload[0].payload;
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.secondary,
            borderColor: theme.palette.text.secondary,
            borderStyle: 'solid',
            borderWidth: '1px',
            padding: '4px 8px',
          }}
        >
          <span>{`${item.name} : ${item.value}`}</span>
        </div>
      );
    }
    return null;
  };

  const getMax = fetchedData
    ? Math.max(
        ...fetchedData.map((d) =>
          Math.max(d.operation_time, d.operating_no_movement, d.idle_time)
        )
      )
    : 0;

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="calc(100% - 38px)"
      width="100%"
      overflow="hidden"
    >
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <DashboardPanelTitleSlot>
        {t(`panels.${panel?.code}`)}
      </DashboardPanelTitleSlot>

      {/* Filters */}
      <Box display="flex" gap={1} py={1}>
        <Box maxWidth={400} minWidth={200}>
          <SafeyeNodeSelector
            multiple
            fullWidth
            size="small"
            value={params.safeye_node_id as unknown as number[]}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    safeye_node_ids: {
                      $set: v as unknown as string[],
                    },
                  },
                })
              );
            }}
          />
        </Box>
        <Box maxWidth={400} minWidth={200}>
          <SitesSelect
            multiple
            fullWidth
            size="small"
            value={params.site_id}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    site_ids: {
                      $set: v as number[],
                    },
                  },
                })
              );
            }}
          />
        </Box>
        <Box minWidth={200}>
          <MachineTypeSelect
            multiple
            fullWidth
            size="small"
            value={params.type_id}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    type_ids: {
                      $set: v as number[],
                    },
                  },
                })
              );
            }}
          />
        </Box>
        <Box maxWidth={400}>
          <DateRangeSelect
            value={[
              dayjs(params.date_start).toDate(),
              dayjs(params.date_end).toDate(),
            ]}
            size="small"
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    date_start: {
                      $set: v?.[0]
                        ? dayjs(v?.[0]).format('YYYY-MM-DD')
                        : undefined,
                    },
                    date_end: {
                      $set: v?.[1]
                        ? dayjs(v?.[1]).format('YYYY-MM-DD')
                        : undefined,
                    },
                  },
                })
              );
            }}
          />
        </Box>

        <Box ml="auto" display="flex" alignItems="center">
          <ButtonGroup>
            <HazardAISummaryGridExportButton
              params={params}
              shownFields={DEFAULT_SHOWN_FIELDS}
              filenamePrefix="hazard_ai_machine_utilization"
            />
          </ButtonGroup>
        </Box>
      </Box>

      {/* Errors */}
      {fetchedErrors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          onClose={() => params && fetchData(params)}
        >
          {error}
        </Alert>
      ))}

      <Box
        display="flex"
        flexDirection="column"
        height="calc(100% - 56px)"
        gap={1}
      >
        {/* Chart */}
        {!fetchedInProgress && hasData && (
          <Box
            width="100%"
            height="50%"
            bgcolor={isDarkMode ? 'background.default' : 'grey.100'}
            display="flex"
            flexDirection="row"
          >
            <>
              <ResponsiveContainer width="25%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius="95%"
                    dataKey="value"
                  >
                    <Cell key="0" fill="blue" />
                    <Cell key="1" fill="orange" />
                    <Cell key="2" fill="red" />
                  </Pie>
                  <TooltipRecharts cursor={false} content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <ResponsiveContainer width="75%" height="100%">
                <BarChart
                  data={fetchedData}
                  maxBarSize={MAX_BAR_SIZE}
                  margin={{
                    top: 30,
                    bottom: 10,
                    right: 10,
                  }}
                >
                  <XAxis dataKey="bucket" />
                  <YAxis domain={[0, getMax]} />
                  {/* <TooltipRecharts cursor={false} content={<Box></Box>} /> */}
                  <Legend />
                  <Bar
                    dataKey="operation_time"
                    name="Operation Time"
                    label={{
                      position: 'top',
                      fill: theme.palette.text.secondary,
                    }}
                    fill="blue"
                  />
                  <Bar
                    dataKey="operating_no_movement"
                    name="Operating with No Movement"
                    label={{
                      position: 'top',
                      fill: theme.palette.text.secondary,
                    }}
                    fill="orange"
                  />
                  <Bar
                    dataKey="idle_time"
                    name="Idle Time"
                    label={{
                      position: 'top',
                      fill: theme.palette.text.secondary,
                    }}
                    fill="red"
                  />
                </BarChart>
              </ResponsiveContainer>
            </>
          </Box>
        )}

        {(fetchedInProgress || !hasData) && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="50%"
            bgcolor={isDarkMode ? 'background.default' : 'grey.100'}
          >
            {fetchedInProgress ? 'Loading...' : 'No data to display'}
          </Box>
        )}

        {/* Grid */}
        <HazardAISummaryGrid
          value={{
            ...props?.value,
            shownFields: props?.value?.shownFields || DEFAULT_SHOWN_FIELDS,
            columnWidths: props?.value?.columnWidths,
            columnOrder: props?.value?.columnOrder,
            selectedIds: props?.value?.selectedIds,
            grid: props?.value?.grid,
            params: props?.value?.params,
          }}
          onChange={(v) => {
            props?.onUpdate?.({
              ...props.value,
              ...v,
            });
          }}
        />
      </Box>
    </Box>
  );
};
